import React, { useState, useRef, useEffect } from "react";
import { useCenter } from "../../peoples/centers/hooks/useCenter";
import { showToast } from "../../../core/utils/alert";
import CommonSelect, { Option } from "../../../core/common/commonSelect";
import { OfflinePayment } from "../api/offlinePaymentApi";

interface Props {
  payment: OfflinePayment;
  onSuccess: () => void;
  onClose: () => void;
  handleUpdate: (id: number | string, formData: FormData) => Promise<any>;
  isUpdating: boolean;
}

const EditOfflinePaymentModal: React.FC<Props> = ({
  payment,
  onSuccess,
  onClose,
  handleUpdate,
  isUpdating,
}) => {
  const { useCenterList } = useCenter({ skip: true });
  const { data: centerResponse } = useCenterList();
  const centers = centerResponse?.data?.items || [];

  const centerOptions = centers.map((c) => ({
    value: c.id.toString(),
    label: c.name,
  }));

  const paymentMethodOptions = [
    { value: "cash", label: "Cash" },
    { value: "mpay", label: "M-Pay" },
    { value: "bank_transfer", label: "Bank Transfer" },
    { value: "cheque", label: "Cheque" },
    { value: "upi", label: "UPI" },
  ];

  const statusOptions = [
    { value: "pending", label: "Pending" },
    { value: "approved", label: "Approved" },
    { value: "rejected", label: "Rejected" },
  ];

  const [form, setForm] = useState({
    student_id: payment.student_id?.toString() || "",
    center_id: payment.center_id?.toString() || "",
    amount: payment.amount?.toString() || "",
    payment_date: payment.payment_date || "",
    payment_method: payment.payment_method || "",
    reference_number: payment.reference_number || "",
    remarks: payment.remarks || "",
    status: payment.status || "pending",
  });
  const [receiptFile, setReceiptFile] = useState<File | null>(null);
  const [receiptPreview, setReceiptPreview] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const handleChange = (field: string, value: string) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setReceiptFile(file);
    if (file && file.type.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setReceiptPreview(reader.result as string);
      reader.readAsDataURL(file);
    } else {
      setReceiptPreview(null);
    }
  };

  const validate = () => {
    const newErrors: Record<string, string> = {};
    if (!form.amount || isNaN(Number(form.amount)) || Number(form.amount) <= 0)
      newErrors.amount = "Valid amount is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const fd = new FormData();
    fd.append("amount", form.amount);
    fd.append("student_id", form.student_id);
    fd.append("center_id", form.center_id);
    if (form.payment_date) fd.append("payment_date", form.payment_date);
    if (form.payment_method) fd.append("payment_method", form.payment_method);
    if (form.reference_number) fd.append("reference_number", form.reference_number);
    fd.append("remarks", form.remarks);
    if (form.status) fd.append("status", form.status);
    if (receiptFile) fd.append("receipt", receiptFile);

    try {
      const res = await handleUpdate(payment.id, fd);
      if (res?.success) {
        showToast({ text: "Payment updated successfully", icon: "success" });
        onSuccess();
        onClose();
      } else {
        showToast({ text: res?.message || "Failed to update payment", icon: "error" });
      }
    } catch (err: any) {
      showToast({
        text: err?.data?.message || "An error occurred",
        icon: "error",
      });
    }
  };

  const currentReceiptName = payment.receipt_path
    ? payment.receipt_path.split("/").pop()
    : null;

  return (
    <>
      <div
        className="modal-backdrop fade show"
        style={{ zIndex: 1040 }}
        onClick={onClose}
      />
      <div
        className="modal fade show d-block"
        tabIndex={-1}
        style={{ zIndex: 1050 }}
        role="dialog"
      >
        <div className="modal-dialog modal-lg modal-dialog-centered" role="document">
          <div
            className="modal-content shadow-lg border-0"
            style={{ borderRadius: "16px", overflow: "hidden" }}
          >
            {/* Header */}
            <div
              className="modal-header border-0 px-4 pt-4 pb-3"
              style={{
                background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
              }}
            >
              <div className="d-flex align-items-center">
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center me-3"
                  style={{ width: 44, height: 44, background: "rgba(255,255,255,0.25)" }}
                >
                  <i className="ti ti-edit fs-20 text-white" />
                </div>
                <div>
                  <h5 className="modal-title text-white mb-0 fw-semibold">
                    Edit Offline Payment
                  </h5>
                  <small className="text-white opacity-75">
                    Payment ID: #{payment.id}
                  </small>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white ms-auto"
                onClick={onClose}
              />
            </div>

            {/* Body */}
            <form onSubmit={handleSubmit}>
              <div className="modal-body px-4 py-4">
                <div className="row g-3">
                  {/* Student ID */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Student ID</label>
                    <input
                      type="number"
                      className="form-control"
                      value={form.student_id}
                      onChange={(e) => handleChange("student_id", e.target.value)}
                    />
                  </div>

                  {/* Center */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Center</label>
                    <CommonSelect
                      className="select"
                      options={centerOptions}
                      value={
                        centerOptions.find((o) => o.value === form.center_id) || null
                      }
                      onChange={(opt: Option | null) =>
                        handleChange("center_id", opt?.value || "")
                      }
                    />
                  </div>

                  {/* Amount */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Amount <span className="text-danger">*</span>
                    </label>
                    <div className="input-group">
                      <span className="input-group-text">₹</span>
                      <input
                        type="number"
                        step="0.01"
                        className={`form-control ${errors.amount ? "is-invalid" : ""}`}
                        value={form.amount}
                        onChange={(e) => handleChange("amount", e.target.value)}
                      />
                      {errors.amount && (
                        <div className="invalid-feedback">{errors.amount}</div>
                      )}
                    </div>
                  </div>

                  {/* Payment Date */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Payment Date</label>
                    <input
                      type="date"
                      className="form-control"
                      value={form.payment_date || ""}
                      onChange={(e) => handleChange("payment_date", e.target.value)}
                    />
                  </div>

                  {/* Payment Method */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Payment Method</label>
                    <CommonSelect
                      className="select"
                      options={paymentMethodOptions}
                      value={
                        paymentMethodOptions.find(
                          (o) => o.value === form.payment_method
                        ) || null
                      }
                      onChange={(opt: Option | null) =>
                        handleChange("payment_method", opt?.value || "")
                      }
                    />
                  </div>

                  {/* Reference Number */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Reference Number</label>
                    <input
                      type="text"
                      className="form-control"
                      value={form.reference_number}
                      onChange={(e) => handleChange("reference_number", e.target.value)}
                    />
                  </div>

                  {/* Remarks */}
                  <div className="col-12">
                    <label className="form-label fw-medium">Remarks</label>
                    <textarea
                      className="form-control"
                      rows={3}
                      placeholder="Any notes or remarks about this payment..."
                      value={form.remarks}
                      onChange={(e) => handleChange("remarks", e.target.value)}
                    />
                  </div>

                  {/* Status */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">Status</label>
                    <CommonSelect
                      className="select"
                      options={statusOptions}
                      value={
                        statusOptions.find((o) => o.value === form.status) || null
                      }
                      onChange={(opt: Option | null) =>
                        handleChange("status", opt?.value || "pending")
                      }
                    />
                  </div>

                  {/* Receipt Upload */}
                  <div className="col-md-6">
                    <label className="form-label fw-medium">
                      Replace Receipt
                      <small className="text-muted ms-1">(optional)</small>
                    </label>
                    {currentReceiptName && !receiptFile && (
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <i className="ti ti-file-description text-primary" />
                        <small className="text-muted">{currentReceiptName}</small>
                      </div>
                    )}
                    <div
                      className="border rounded-3 p-3 text-center"
                      style={{
                        cursor: "pointer",
                        background: receiptFile ? "#f8fff9" : "#fafafa",
                        borderStyle: "dashed",
                        minHeight: 80,
                      }}
                      onClick={() => fileInputRef.current?.click()}
                    >
                      {receiptPreview ? (
                        <img
                          src={receiptPreview}
                          alt="Receipt preview"
                          style={{ maxHeight: 100, borderRadius: 8 }}
                        />
                      ) : receiptFile ? (
                        <div className="d-flex align-items-center justify-content-center gap-2 py-2">
                          <i className="ti ti-file-description fs-22 text-success" />
                          <span className="text-muted small">{receiptFile.name}</span>
                        </div>
                      ) : (
                        <div className="py-2">
                          <i className="ti ti-cloud-upload fs-24 text-muted mb-1" />
                          <p className="text-muted small mb-0">Click to upload new receipt</p>
                        </div>
                      )}
                    </div>
                    <input
                      type="file"
                      accept="image/jpeg,image/png,application/pdf"
                      ref={fileInputRef}
                      className="d-none"
                      onChange={handleFileChange}
                    />
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="modal-footer border-0 px-4 pb-4 pt-2">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={onClose}
                  disabled={isUpdating}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="btn btn-success px-4 d-flex align-items-center gap-2"
                  disabled={isUpdating}
                  style={{
                    background: "linear-gradient(135deg, #11998e 0%, #38ef7d 100%)",
                    border: "none",
                  }}
                >
                  {isUpdating ? (
                    <>
                      <span className="spinner-border spinner-border-sm" role="status" />
                      Updating...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-device-floppy" />
                      Update Payment
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditOfflinePaymentModal;
